// src/hr/portal/SettingsPage.js -- HR Portal settings (mockup screen 5): who has HR access, the backup
// approver, the benefit year and Sick & Safe rules, time-off types, company holidays, and a way into
// the year-end close-out.
//   HR access: every HR person sees the list (R21); only the owner (me.isOwner) gets add / remove, and
//   owner rows carry a lock badge and can never be removed (they come from HR_OWNER_EMAILS).
//   Server validation messages show inline, next to the field they belong to where we can tell.
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiAlertTriangle, FiArrowRight, FiCheck, FiLock, FiShield, FiUserPlus } from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Avatar, Btn, Card, Field, Pill, Skeleton } from '../kit';
import { plainId } from '../kit/util';
import { shortDate } from '../timeoff/dates';
import { EMAIL_RE, fullName, isCurrent, personForEmail, stampLabel } from './portalUtils';
import { HolidaysCard, TypesCard } from './SettingsLists';
import { FormError } from './FormError';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const two = (n) => String(n).padStart(2, '0');

// ---------------------------------------------------------------------------------------------
// HR access
// ---------------------------------------------------------------------------------------------

// 'mia.park@solum-group.com' -> 'mia park' (only for the avatar's initials when nobody matches).
const localName = (email) => String(email || '').split('@')[0].replace(/[._-]+/g, ' ');

function AccessRow({ row, people, mine, canChange, onRemoved }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const keepRef = useRef(null);
  const person = personForEmail(people, row.email);
  const you = mine.has(row.email.toLowerCase());
  const name = you ? 'You' : person ? fullName(person) : row.email;
  const added = row.addedAt ? stampLabel(row.addedAt).split(' · ')[0] : null;

  useEffect(() => {
    if (confirming && keepRef.current) keepRef.current.focus();
  }, [confirming]);

  const remove = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await hr.admin.access.remove(row.email);
      onRemoved(row.email, result && result.access);
    } catch (err) {
      setBusy(false);
      setError(errorText(err, "Couldn't remove HR access. Try again."));
    }
  };

  return (
    <li className="hrp-acc__it">
      <div className="hrp-acc__row">
        <Avatar id={person ? person.id : row.email} name={person ? fullName(person) : localName(row.email)} />
        <div className="hrp-acc__txt">
          <b>{name}</b>
          <span>
            {name !== row.email && <><span className="hrp-acc__mail">{row.email}</span>{' · '}</>}
            {row.owner ? "can't be removed" : added ? `added ${added}` : 'full HR access'}
          </span>
        </div>
        {row.owner && <Pill tone="pto" icon={<FiLock />} className="hrp-acc__lock">Owner</Pill>}
        {!row.owner && !canChange && <span className="hrp-acc__role">Full HR access</span>}
        {!row.owner && canChange && !confirming && (
          <button type="button" className="hrp-link hrp-acc__rm" aria-label={`Remove ${row.email}`} onClick={() => setConfirming(true)}>Remove</button>
        )}
      </div>
      {confirming && (
        <div className="hrp-tl__confirm hrp-acc__confirm" role="group" aria-label={`Remove HR access for ${row.email}?`}>
          <span>{`Remove HR access for ${person ? fullName(person) : row.email}? They lose the HR Portal right away.`}</span>
          <span className="hrp-tl__btns">
            <Btn ref={keepRef} variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={busy}>Keep</Btn>
            <Btn variant="danger" size="sm" loading={busy} onClick={remove}>Remove access</Btn>
          </span>
        </div>
      )}
      {error && <p className="hrp-al__err" role="alert"><FiAlertCircle aria-hidden="true" />{error}</p>}
    </li>
  );
}

function AccessCard({ me, people, onToast }) {
  const isOwner = !!(me && me.isOwner);
  const [state, setState] = useState({ status: 'loading', rows: [] });
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // The server's heads-up after an add (m2: the address is on nobody's people record).
  const [notice, setNotice] = useState('');
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      const body = await hr.admin.access.list();
      setState({ status: 'ready', rows: (body && body.access) || [] });
    } catch (err) {
      setState((s) => ({ ...s, status: 'error' }));
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (adding && inputRef.current) inputRef.current.focus();
  }, [adding]);

  const all = useMemo(() => (people && people.data && people.data.people) || [], [people]);
  const mine = useMemo(() => {
    const self = me && me.employee ? all.find((p) => p.id === me.employee.id) : null;
    return new Set(self ? [self.email, ...(self.emails || [])].filter(Boolean).map((e) => e.toLowerCase()) : []);
  }, [all, me]);

  const add = async (e) => {
    e.preventDefault();
    const address = email.trim();
    if (!EMAIL_RE.test(address)) {
      setFieldError('Enter a valid email');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await hr.admin.access.add({ email: address });
      if (result && Array.isArray(result.access)) setState({ status: 'ready', rows: result.access });
      else load();
      setNotice((result && typeof result.warning === 'string' && result.warning) || '');
      setAdding(false);
      setEmail('');
      onToast(`HR access given to ${address.toLowerCase()}`);
    } catch (err) {
      setError(errorText(err, "Couldn't give HR access. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const removed = (address, rows) => {
    if (Array.isArray(rows)) setState({ status: 'ready', rows });
    else setState((s) => ({ ...s, rows: s.rows.filter((r) => r.email !== address) }));
    setNotice('');
    onToast(`HR access removed for ${address}`);
  };

  return (
    <Card
      delay={0}
      as="section"
      aria-label="HR access"
      title="HR access"
      className="hrp-acc"
      action={isOwner && !adding && (
        <button type="button" className="hrp-link" onClick={() => { setAdding(true); setError(''); setFieldError(''); setNotice(''); }}>
          <FiUserPlus aria-hidden="true" /> Add HR person
        </button>
      )}
    >
      {state.status === 'loading' && state.rows.length === 0 && <Skeleton label="Loading HR access" lines={3} height={14} />}
      {state.status === 'error' && (
        <div className="hrp-wo-err" role="alert">
          <FiAlertCircle aria-hidden="true" />
          <span>Couldn't load the HR access list.</span>
          <Btn variant="ghost" size="sm" onClick={load}>Try again</Btn>
        </div>
      )}
      {state.rows.length > 0 && (
        <ul className="hrp-acc__list">
          {state.rows.map((row) => (
            <AccessRow key={row.email} row={row} people={all} mine={mine} canChange={isOwner} onRemoved={removed} />
          ))}
        </ul>
      )}
      {notice && (
        <p className="hrp-set__warn hrp-acc__warn" role="status">
          <FiAlertTriangle aria-hidden="true" />
          <span>{notice}</span>
        </p>
      )}

      {isOwner && adding && (
        <form className="hrp-acc__add" onSubmit={add} noValidate>
          <Field label="Work email" error={fieldError} hint="The address they sign in with. The HR Portal appears for them on their next visit.">
            <input
              ref={inputRef}
              className="hrp-input"
              type="email"
              value={email}
              autoComplete="off"
              maxLength={254}
              onChange={(e) => { setEmail(e.target.value); setFieldError(''); setError(''); }}
            />
          </Field>
          <FormError>{error}</FormError>
          <div className="hrp-acc__addbtns">
            <Btn variant="ghost" size="sm" onClick={() => { setAdding(false); setEmail(''); setError(''); }} disabled={busy}>Cancel</Btn>
            <Btn type="submit" size="sm" icon={FiCheck} loading={busy}>Give HR access</Btn>
          </div>
        </form>
      )}

      <p className="hrp-acc__note">
        <FiShield aria-hidden="true" />
        <span>
          Super admins don't get HR access automatically. Only the people on this list can open the HR side or see anyone else's time off.
          {!isOwner && <span className="hrp-acc__only">Only the HR owner can add or remove HR people.</span>}
        </span>
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------------------------
// Backup approver
// ---------------------------------------------------------------------------------------------

function backupForm(settings) {
  const b = settings && settings.backup;
  return { employeeId: b ? b.employeeId : '', from: (b && b.from) || '', to: (b && b.to) || '' };
}

function BackupCard({ settings, people, context, onToast }) {
  const saved = useMemo(() => backupForm(settings), [settings]);
  const [form, setForm] = useState(saved);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const savedKey = `${saved.employeeId}|${saved.from}|${saved.to}`;
  const savedRef = useRef(savedKey);
  useEffect(() => {
    if (savedRef.current !== savedKey) {
      savedRef.current = savedKey;
      setForm(saved);
    }
  }, [savedKey, saved]);

  const all = (people && people.data && people.data.people) || [];
  const current = all.find((p) => p.id === saved.employeeId) || null;
  const choices = all.filter(isCurrent).map((p) => ({ value: p.id, label: fullName(p) })).sort((a, b) => a.label.localeCompare(b.label));
  if (form.employeeId && !choices.some((c) => c.value === form.employeeId)) {
    const gone = all.find((p) => p.id === form.employeeId);
    choices.unshift({ value: form.employeeId, label: gone ? `${fullName(gone)} (no longer here)` : 'Someone no longer here' });
  }
  const changed = `${form.employeeId}|${form.from}|${form.to}` !== savedKey;
  const set = (key) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [key]: value }));
    setError('');
  };

  const save = async (e) => {
    e.preventDefault();
    if (!changed || busy) return;
    const backup = form.employeeId ? { employeeId: form.employeeId, from: form.from || null, to: form.to || null } : null;
    setBusy(true);
    setError('');
    try {
      await hr.admin.settings.update({ backup });
      onToast(backup ? 'Backup approver saved' : 'Backup approver removed');
      await context.reload();
    } catch (err) {
      setError(errorText(err, "Couldn't save the backup approver. Try again."));
    } finally {
      setBusy(false);
    }
  };

  let status;
  if (current) {
    const when = saved.from || saved.to
      ? `Covers HR ${saved.from ? shortDate(saved.from) : 'from now'} – ${saved.to ? shortDate(saved.to) : 'until removed'}`
      : 'Covers HR at all times';
    status = (
      <div className="hrp-rq-setup__pick">
        <Avatar id={current.id} name={fullName(current)} size="sm" />
        <div><b>{fullName(current)}</b><span>{when}</span></div>
      </div>
    );
  } else {
    status = (
      <div className="hrp-rq-setup__pick" data-tone="warn">
        <FiAlertCircle aria-hidden="true" />
        <div><b>None set</b><span>HR's own requests can't be signed off until you choose one.</span></div>
      </div>
    );
  }

  return (
    <Card delay={1} as="section" aria-label="Backup approver" title="Backup approver" className="hrp-set__backup">
      <p className="hrp-set__lead">Signs off HR's own requests, and everyone's HR step while HR is away (inside the dates).</p>
      {settings ? status : <Skeleton lines={2} height={14} />}
      <form className="hrp-set__form" onSubmit={save} noValidate>
        <Field
          label="Backup approver"
          type="select"
          value={form.employeeId}
          onChange={set('employeeId')}
          options={[{ value: '', label: 'No backup approver' }, ...choices]}
        />
        <div className="hrp-two">
          <Field label="From" labelNote="(optional)" type="date" value={form.from} onChange={set('from')} disabled={!form.employeeId} />
          <Field label="To" labelNote="(optional)" type="date" value={form.to} onChange={set('to')} disabled={!form.employeeId} />
        </div>
        <FormError>{error}</FormError>
        <div className="hrp-set__foot">
          {changed && <Btn variant="ghost" size="sm" onClick={() => { setForm(saved); setError(''); }} disabled={busy}>Undo</Btn>}
          <Btn type="submit" size="sm" icon={FiCheck} loading={busy} disabled={!changed}>Save backup</Btn>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------------------------
// Benefit year + Sick & Safe rules
// ---------------------------------------------------------------------------------------------

function rulesForm(settings) {
  const md = /^\d{2}-\d{2}$/.test(String((settings && settings.benefitYearStart) || '')) ? settings.benefitYearStart : '01-01';
  return {
    month: md.slice(0, 2),
    day: md.slice(3),
    waiting: String(settings ? Number(settings.sickWaitingDays) || 0 : 0),
    cap: !!(settings && settings.sickUseCap40)
  };
}

const FIELD_OF_MESSAGE = {
  'Pick a valid benefit year start': 'start',
  'Waiting period must be 0–120 days': 'waiting'
};

// The server's words (hrConfig.updateSettings warning), shown before a benefit-year change is saved (m5).
const NJDOL_NOTICE = "NJ requires 30 days' written notice to NJDOL before changing the benefit year";

function RulesCard({ settings, context, onToast }) {
  const ids = `hrp-rules-${plainId(useId())}`;
  const [baseline, setBaseline] = useState(settings);
  const saved = useMemo(() => rulesForm(baseline), [baseline]);
  const [form, setForm] = useState(saved);
  const [errors, setErrors] = useState({});
  const [warning, setWarning] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmStart, setConfirmStart] = useState(false);
  const keepStartRef = useRef(null);
  const savedKey = JSON.stringify(saved);
  const savedRef = useRef(savedKey);

  useEffect(() => {
    if (confirmStart && keepStartRef.current) keepStartRef.current.focus();
  }, [confirmStart]);

  // A fresh settings object from the app (after any save) becomes the new baseline.
  useEffect(() => { if (settings) setBaseline(settings); }, [settings]);
  useEffect(() => {
    if (savedRef.current !== savedKey) {
      savedRef.current = savedKey;
      setForm(saved);
    }
  }, [savedKey, saved]);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors({});
    setConfirmStart(false);
  };

  const body = {};
  if (`${form.month}-${form.day}` !== `${saved.month}-${saved.day}`) body.benefitYearStart = `${form.month}-${form.day}`;
  if (form.waiting.trim() !== saved.waiting) {
    const n = Number(form.waiting);
    body.sickWaitingDays = form.waiting.trim() !== '' && Number.isFinite(n) ? n : form.waiting;
  }
  if (form.cap !== saved.cap) body.sickUseCap40 = form.cap;
  const changed = Object.keys(body).length > 0;

  const persist = async () => {
    if (!changed || busy) return;
    setBusy(true);
    setErrors({});
    try {
      const result = await hr.admin.settings.update(body);
      setConfirmStart(false);
      if (result && result.settings) setBaseline(result.settings);
      setWarning((result && result.warning) || '');
      onToast('Rules saved');
      context.reload();
    } catch (err) {
      const message = errorText(err, "Couldn't save the rules. Try again.");
      setConfirmStart(false);
      setErrors({ [FIELD_OF_MESSAGE[message] || 'form']: message });
    } finally {
      setBusy(false);
    }
  };

  // A new benefit year start is confirmed first, with the NJDOL notice rule in view (m5).
  const save = (e) => {
    e.preventDefault();
    if (!changed || busy) return;
    if (body.benefitYearStart && !confirmStart) {
      setConfirmStart(true);
      return;
    }
    persist();
  };
  const startLabel = `${MONTHS[Number(form.month) - 1] || form.month} ${Number(form.day)}`;

  if (!settings && !baseline) {
    return (
      <Card delay={2} as="section" aria-label="Benefit year & Sick & Safe" title="Benefit year & Sick & Safe">
        <Skeleton label="Loading the rules" lines={4} height={14} />
      </Card>
    );
  }

  return (
    <Card delay={2} as="section" aria-label="Benefit year & Sick & Safe" title="Benefit year & Sick & Safe" className="hrp-set__rules">
      <form className="hrp-set__form" onSubmit={save} noValidate>
        <fieldset className="hrp-set__fs" aria-describedby={errors.start ? `${ids}-start-err` : `${ids}-start-hint`}>
          <legend className="hrp-field__label">Benefit year starts</legend>
          <div className="hrp-set__md">
            <Field label="Month" type="select" value={form.month} onChange={(e) => set('month', e.target.value)}
              options={MONTHS.map((m, i) => ({ value: two(i + 1), label: m }))} aria-invalid={errors.start ? 'true' : undefined} />
            <Field label="Day" type="select" value={form.day} onChange={(e) => set('day', e.target.value)}
              options={Array.from({ length: 31 }, (_, i) => ({ value: two(i + 1), label: String(i + 1) }))} aria-invalid={errors.start ? 'true' : undefined} />
          </div>
          {errors.start
            ? <div id={`${ids}-start-err`} className="hrp-field__err" role="alert">{errors.start}</div>
            : <div id={`${ids}-start-hint`} className="hrp-field__hint">Everyone's 40 Sick &amp; Safe hours are granted on this day. Changing it needs advance notice to the state.</div>}
        </fieldset>
        {confirmStart && (
          <div className="hrp-set__warn hrp-set__ycf" role="group" aria-label="Change the benefit year start?">
            <FiAlertTriangle aria-hidden="true" />
            <div className="hrp-set__ycftxt">
              <b>{`Change the benefit year start to ${startLabel}?`}</b>
              <span>{NJDOL_NOTICE}</span>
              <div className="hrp-set__ycfbtns">
                <Btn ref={keepStartRef} variant="ghost" size="sm" onClick={() => setConfirmStart(false)} disabled={busy}>Keep the current start</Btn>
                <Btn size="sm" icon={FiCheck} loading={busy} onClick={persist}>Change the start date</Btn>
              </div>
            </div>
          </div>
        )}
        {warning && !confirmStart && (
          <p className="hrp-set__warn" role="status">
            <FiAlertTriangle aria-hidden="true" />
            <span>{warning}</span>
          </p>
        )}

        <Field
          label="Waiting period"
          labelNote="(days after hire)"
          type="number"
          min={0}
          max={120}
          step={1}
          inputMode="numeric"
          value={form.waiting}
          onChange={(e) => set('waiting', e.target.value)}
          error={errors.waiting}
          hint="0–120. Sick days before the hire date plus this many days are refused."
          className="hrp-set__wait"
        />

        <div className="hrp-set__cap">
          <div className="hrp-set__captxt">
            <b id={`${ids}-cap`}>Cap Sick &amp; Safe use at 40 hours a year</b>
            <span id={`${ids}-cap-hint`}>When on, sick time beyond 40 hours used in a benefit year is refused. Off by default.</span>
          </div>
          <button
            type="button"
            className="hrp-switch hrp-switch--accent"
            role="switch"
            aria-checked={form.cap}
            aria-labelledby={`${ids}-cap`}
            aria-describedby={`${ids}-cap-hint`}
            onClick={() => set('cap', !form.cap)}
          >
            <span className="hrp-switch__knob" aria-hidden="true" />
          </button>
        </div>

        <FormError>{errors.form}</FormError>
        <div className="hrp-set__foot">
          {changed && <Btn variant="ghost" size="sm" onClick={() => { setForm(saved); setErrors({}); }} disabled={busy}>Undo</Btn>}
          <Btn type="submit" size="sm" icon={FiCheck} loading={busy && !confirmStart} disabled={!changed || confirmStart}>Save rules</Btn>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------------------------

function YearEndCard({ yearRange }) {
  const year = yearRange && yearRange.year;
  return (
    <Card feat delay={5} as="section" aria-label="Year-end close-out" className="hrp-set__ye">
      <div className="hrp-ye__yr">
        <span>Year-end close-out</span>
        {year && <span className="hrp-ye__arrow">{`${year} → ${year + 1}`}</span>}
      </div>
      <p className="hrp-set__lead">Leftover days are filled in for you. Change any number, then confirm. Nothing changes until you start the new year.</p>
      <Link to="../year-end" className="hrp-btn hrp-btn--ghost hrp-btn--sm hrp-set__yebtn">
        <span className="hrp-btn__label">Open the close-out</span>
        <span className="hrp-btn__ic" aria-hidden="true"><FiArrowRight /></span>
      </Link>
    </Card>
  );
}

export function SettingsPage({ me, people, types, holidays, settings, context, yearRange, onToast }) {
  return (
    <div className="hrp-page hrp-set">
      <div className="hrp-page-head">
        <div>
          <h1 className="hrp-h1">Settings</h1>
          <p className="hrp-sub">Who has HR access, who covers for HR, the time-off rules, types and company holidays.</p>
        </div>
      </div>
      {context && context.status === 'error' && !context.data && (
        <div className="hrp-form-err hrp-set__stale" role="alert">
          <FiAlertCircle aria-hidden="true" />
          <span>Couldn't load the settings.</span>
          <Btn variant="ghost" size="sm" onClick={context.reload}>Try again</Btn>
        </div>
      )}
      <div className="hrp-set__cols">
        <div className="hrp-stack">
          <AccessCard me={me} people={people} onToast={onToast} />
          <BackupCard settings={settings} people={people} context={context} onToast={onToast} />
          <RulesCard settings={settings} context={context} onToast={onToast} />
        </div>
        <div className="hrp-stack">
          <TypesCard types={types} loading={!context || !context.data} context={context} onToast={onToast} />
          <HolidaysCard holidays={holidays} loading={!context || !context.data} yearRange={yearRange} context={context} onToast={onToast} />
          <YearEndCard yearRange={yearRange} />
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
